import { Navigate, Route, Routes, useLocation } from 'react-router';
import { Layout } from './components/Layout';
import { StatusBar } from './components/StatusBar';
import { Toaster } from './components/Toaster';
import { UpdateBanner } from './components/UpdateBanner';
import { AdminPage } from './features/admin/AdminPage';
import { NotificationsPage } from './features/notifications/NotificationsPage';
import { OffersPage } from './features/offers/OffersPage';
import { BrandReportPage } from './features/admin/BrandReportPage';
import { AuthPage } from './features/auth/AuthPage';
import { CreateEventPage, EditEventPage } from './features/events/CreateEventPage';
import { EventDetailPage } from './features/events/EventDetailPage';
import { EventsPage } from './features/events/EventsPage';
import { StravaAuthPage } from './features/auth/StravaAuthPage';
import { ClubDetailPage } from './features/clubs/ClubDetailPage';
import { ClubsPage } from './features/clubs/ClubsPage';
import { JoinClubPage } from './features/clubs/JoinClubPage';
import { HomePage } from './features/home/HomePage';
import { GuidePage } from './features/guide/GuidePage';
import { ContactPage, PrivacyPage } from './features/legal/LegalPages';
import { StravaImportPage } from './features/imports/StravaImportPage';
import { LeaderboardPage } from './features/leaderboard/LeaderboardPage';
import { MapPage } from './features/map/MapPage';
import { PlayerPage } from './features/profile/PlayerPage';
import { PlayerRunsPage } from './features/profile/PlayerRunsPage';
import { BadgesPage } from './features/achievements/BadgesPage';
import { ProfilePage } from './features/profile/ProfilePage';
import { EditProfilePage } from './features/profile/EditProfilePage';
import { AccountPage } from './features/profile/AccountPage';
import { RivalPage, RivalsPage } from './features/rivals/RivalsPages';
import { RunDetailPage } from './features/running/RunDetailPage';
import { RunPage } from './features/running/RunPage';
import { RuntimeProvider } from './hooks/useRuntime';
import { useAuth } from './stores/auth';

function Protected({ children }: { children: React.ReactNode }) {
  const token = useAuth((s) => s.token);
  const location = useLocation();
  if (!token) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <RuntimeProvider>{children}</RuntimeProvider>;
}

function PublicOnly({ children }: { children: React.ReactNode }) {
  const token = useAuth((s) => s.token);
  return token ? <Navigate to="/" replace /> : <>{children}</>;
}

export function App() {
  return (
    <>
      <StatusBar />
      <UpdateBanner />
      <Toaster />
      <Routes>
        <Route path="/login" element={<PublicOnly><AuthPage mode="login" /></PublicOnly>} />
        <Route path="/register" element={<PublicOnly><AuthPage mode="register" /></PublicOnly>} />
        <Route path="/auth/strava" element={<StravaAuthPage />} />
        <Route path="/guide" element={<GuidePage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route
          element={
            <Protected>
              <Layout />
            </Protected>
          }
        >
          <Route index element={<HomePage />} />
          <Route path="map" element={<MapPage />} />
          <Route path="run" element={<RunPage />} />
          <Route path="runs/:id" element={<RunDetailPage />} />
          <Route path="leaderboard" element={<LeaderboardPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="profile/edit" element={<EditProfilePage />} />
          <Route path="profile/account" element={<AccountPage />} />
          <Route path="rivals" element={<RivalsPage />} />
          <Route path="rivals/:id" element={<RivalPage />} />
          <Route path="players/:id" element={<PlayerPage />} />
          <Route path="players/:id/runs" element={<PlayerRunsPage />} />
          <Route path="badges" element={<BadgesPage />} />
          <Route path="players/:id/badges" element={<BadgesPage />} />
          <Route path="clubs" element={<ClubsPage />} />
          <Route path="clubs/:id" element={<ClubDetailPage />} />
          <Route path="join/:code" element={<JoinClubPage />} />
          <Route path="imports/strava" element={<StravaImportPage />} />
          <Route path="events" element={<EventsPage />} />
          <Route path="events/new" element={<CreateEventPage />} />
          <Route path="events/:id/edit" element={<EditEventPage />} />
          <Route path="events/:id" element={<EventDetailPage />} />
          <Route path="e/:code" element={<EventDetailPage />} />
          <Route path="admin" element={<AdminPage />} />
          <Route path="admin/reports/:brandId" element={<BrandReportPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="offers" element={<OffersPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
